import { Box, Button, Flex, Grid, Heading, HStack, Icon, List, Text } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FiArrowRight, FiCheckCircle, FiLock } from "react-icons/fi";

import { BrandMark, Highlight } from "../components/ui";
import { duration, ease, gradients, reducedTransition, useReducedMotion } from "../theme";

const STAGE_BULLETS = ["Applications & credit", "Contracts & consumption", "Stock & spend insight"];

/**
 * Story timeline (§9.5) — everything animates on the navy stage only; the card and CTA are visible from frame 0.
 * 0.00s logo ink wipe (0.9s) → 0.30s headline rises → 0.70s highlighter swipe → 1.00s bullets, 60ms apart.
 */
const HEADLINE_DELAY = 0.3;
const HIGHLIGHT_DELAY = 0.7;
const BULLETS_DELAY = 1.0;
const BULLET_STAGGER = 0.06;

/** Slow drift of the ink blob — the only infinite animation in the app (§10). */
const BLOB_DRIFT = { x: [0, 40, -20, 0], y: [0, -30, 20, 0] };

export const LoginPage = () => {
  const reduce = useReducedMotion();

  const handleRedirect = (e: any) => {
    e.preventDefault();
    window.location.href = "https://partner.bidfoodme.com/api/v1/auth/login-redirect";
  };

  return (
    <Grid
      as="main"
      minH="100vh"
      templateColumns={{ base: "1fr", lg: "55fr 45fr" }}
      templateRows={{ base: "auto 1fr", lg: "1fr" }}
      bg="surface.canvas"
    >
      {/* Brand stage — a 220px band below lg, the full-height left column from lg */}
      <Flex
        position="relative"
        overflow="hidden"
        direction="column"
        justify="center"
        minH={{ base: "220px", lg: "100vh" }}
        bg={gradients.loginStage}
        color="text.inverse"
        px={{ base: 4, sm: 6, lg: 12, xl: 20 }}
        pt={{ base: 10, lg: 0 }}
        pb={{ base: 16, lg: 0 }}
      >
        {/* Green ink blob — the only blurred element in the app; static under reduced motion */}
        <motion.div
          aria-hidden
          style={{
            position: "absolute",
            top: "-10%",
            left: "-10%",
            width: 640,
            height: 640,
            borderRadius: "50%",
            background: "radial-gradient(circle at 30% 30%, rgba(107,191,89,0.55), rgba(107,191,89,0) 60%)",
            filter: "blur(40px)",
            pointerEvents: "none",
          }}
          animate={reduce ? { x: 0, y: 0 } : BLOB_DRIFT}
          transition={reduce ? reducedTransition : { duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />

        <Box position="relative" zIndex={1} maxW="36rem">
          {/* Ink wipe: the wordmark is revealed left → right behind a 2px green pen edge */}
          <Box position="relative" display="inline-block" lineHeight={0} overflow="hidden">
            <motion.div
              style={{ display: "inline-block" }}
              initial={reduce ? false : { clipPath: "inset(0 100% 0 0)" }}
              animate={{ clipPath: "inset(0 0% 0 0)" }}
              transition={reduce ? reducedTransition : { duration: duration.storyLong, ease: ease.emphasized }}
            >
              <BrandMark variant="dark" h={{ base: "3rem", lg: "4.5rem" }} />
            </motion.div>
            {!reduce && (
              <motion.span
                aria-hidden
                style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: "100%",
                  pointerEvents: "none",
                  background: "linear-gradient(90deg, transparent calc(100% - 2px), var(--chakra-colors-leaf-500) calc(100% - 2px))",
                }}
                initial={{ x: "-100%", opacity: 1 }}
                animate={{ x: ["-100%", "0%", "0%"], opacity: [1, 1, 0] }}
                transition={{ duration: duration.storyLong, ease: ease.emphasized, times: [0, 0.85, 1] }}
              />
            )}
          </Box>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={
              reduce ? reducedTransition : { duration: duration.slow, ease: ease.standard, delay: HEADLINE_DELAY }
            }
          >
            <Text
              as="p"
              textStyle="display.hero"
              color="text.inverse"
              mt={{ base: 5, lg: 10 }}
            >
              Your Bidfood,{" "}
              <Highlight onDark delay={HIGHLIGHT_DELAY}>
                on one page
              </Highlight>
              .
            </Text>
          </motion.div>

          <List display={{ base: "none", lg: "block" }} spacing={3} mt={8}>
            {STAGE_BULLETS.map((bullet, index) => (
              <motion.li
                key={bullet}
                style={{ display: "flex", alignItems: "center", gap: 12 }}
                initial={reduce ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={
                  reduce
                    ? reducedTransition
                    : {
                        duration: duration.slow,
                        ease: ease.standard,
                        delay: BULLETS_DELAY + index * BULLET_STAGGER,
                      }
                }
              >
                <Icon as={FiCheckCircle} boxSize="20px" color="leaf.500" flexShrink={0} aria-hidden />
                <Text as="span" textStyle="body.lg" color="text.inverseMuted">
                  {bullet}
                </Text>
              </motion.li>
            ))}
          </List>
        </Box>

        <Text
          display={{ base: "none", lg: "block" }}
          position="absolute"
          bottom={8}
          left={{ lg: 12, xl: 20 }}
          textStyle="caption"
          color="navy.300"
        >
          © Bidfood Middle East
        </Text>
      </Flex>

      {/* Action column — dot-grid canvas with the sign-in card; overlaps the band by 40px below lg */}
      <Flex
        align={{ base: "flex-start", lg: "center" }}
        justify="center"
        bg="surface.canvas"
        backgroundImage={gradients.dotGrid}
        backgroundSize="24px 24px"
        px={4}
        pt={{ base: 0, lg: 10 }}
        pb={10}
      >
        <Box position="relative" zIndex={1} w="min(440px, 100%)" mt={{ base: "-40px", lg: 0 }}>
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={reduce ? reducedTransition : { duration: 0.32, ease: ease.standard }}
          >
            <Box bg="surface.card" borderRadius="xl" boxShadow="xl" p={{ base: 6, md: 10 }}>
              <BrandMark variant="light" h="32px" />

              <Text textStyle="label" color="text.muted" mt={8}>
                Partner Portal
              </Text>
              <Heading as="h1" size="lg" mt={1}>
                Sign in
              </Heading>
              <Text textStyle="body.md" color="text.secondary" mt={2}>
                Continue with your myBidfood account. You will be redirected to Bidfood single sign-on.
              </Text>

              {/* data-group (not role="group") keeps the button's semantics while enabling _groupHover on the arrow */}
              <Button
                variant="primary"
                size="lg"
                w="full"
                h="3.5rem"
                mt={8}
                data-group
                onClick={handleRedirect}
                rightIcon={
                  <Icon
                    as={FiArrowRight}
                    boxSize="20px"
                    transition={`transform ${duration.fast * 1000}ms cubic-bezier(0.2, 0.8, 0.2, 1)`}
                    _groupHover={{ transform: "translateX(4px)" }}
                  />
                }
              >
                Sign in with myBidfood
              </Button>

              <HStack spacing={1.5} mt={4} color="text.muted" align="flex-start">
                <Icon as={FiLock} boxSize="12px" mt="0.2em" flexShrink={0} aria-hidden />
                <Text textStyle="caption">Single sign-on · no password is stored in this portal.</Text>
              </HStack>

              <Box borderTop="1px solid" borderColor="border.default" mt={6} pt={5}>
                <Text textStyle="body.sm" color="text.secondary">
                  Need access? Contact your BDE.
                </Text>
              </Box>
            </Box>
          </motion.div>
        </Box>
      </Flex>
    </Grid>
  );
};

export default LoginPage;
